import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import {
  ChargingTokenDesignCard,
  ChargingTokenDesignCardSkeleton,
  IFButton,
  IFDialog,
  IFModal,
  IFText,
} from 'Components'
import { AddChargingTokenDesignModal } from 'Containers'
import { useTranslation } from 'react-i18next'
import { Scrollbars } from 'react-custom-scrollbars'
import Styles from './ChargingTokenDesignGrid.module.css'
import AuthSelectors from 'Stores/Auth/Selectors'
import ChargingTokenDesignActions from 'Stores/ChargingTokenDesign/Actions'
import ChargingTokenDesignSelectors from 'Stores/ChargingTokenDesign/Selectors'
import RequestState from 'Enums/RequestState'
import { Colors } from 'Theme'
import colors from 'Theme/Colors'
import { Actions, Subjects } from 'Enums/permissionKeys'

const ChargingTokenDesignGrid = ({
  chargingTokenDesigns,
  fetchChargingTokenDesigns,
  fetchChargingTokenDesignsRequestState,
  paginationOffset,
  editingAdminRole,
  editChargingTokenDesignRequestState,
  deleteChargingTokenDesign,
  deleteChargingTokenDesignRequestState,
}) => {
  const { t } = useTranslation()
  const [loadingMore, setLoadingMore] = useState(false)
  const canEditToken = editingAdminRole?.can(
    Actions.EDIT,
    Subjects.CHARGING_TOKENS_DESIGN,
  )
  const isLoading =
    fetchChargingTokenDesignsRequestState === RequestState.LOADING
  const editDesignModalRef = useRef()
  const confirmDeleteDialog = useRef()
  const [selectedDesign, setSelectedDesign] = useState(null)
  const [deleteDesignId, setDeleteDesignId] = useState(null)

  const [height, setHeight] = useState(250)

  useEffect(() => {
    const updateHeight = () => {
      const baseWidth = 1710
      const minWidth = 1470
      const minHeight = 200
      const maxHeight = 250

      const screenWidth = window.innerWidth

      let newHeight =
        minHeight +
        ((screenWidth - minWidth) / (baseWidth - minWidth)) *
          (maxHeight - minHeight)

      newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight))

      setHeight(newHeight)
    }

    updateHeight()
    window.addEventListener('resize', updateHeight)

    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  const handleEditDesign = (design) => {
    setSelectedDesign(design)
    editDesignModalRef.current.show()
  }
  const onDeleteClickHandler = (id) => {
    setDeleteDesignId(id)
    confirmDeleteDialog.current.show()
  }
  const handleDeleteAdmin = () => {
    deleteChargingTokenDesign(deleteDesignId)
  }

  const loadMoreData = useCallback(() => {
    if (
      fetchChargingTokenDesignsRequestState !== RequestState.LOADING &&
      paginationOffset !== null
    ) {
      setLoadingMore(true)
      fetchChargingTokenDesigns(paginationOffset)
    }
  }, [fetchChargingTokenDesignsRequestState, paginationOffset])

  useEffect(() => {
    if (editChargingTokenDesignRequestState === RequestState.SUCCEEDED) {
      editDesignModalRef.current.dismiss()
    }
  }, [editChargingTokenDesignRequestState])

  useEffect(() => {
    if (fetchChargingTokenDesignsRequestState !== RequestState.LOADING) {
      setLoadingMore(false)
    }
  }, [fetchChargingTokenDesignsRequestState])
  useEffect(() => {
    if (deleteChargingTokenDesignRequestState === RequestState.SUCCEEDED) {
      confirmDeleteDialog.current.dismiss()
    }
  }, [deleteChargingTokenDesignRequestState])
  useEffect(() => {
    if (chargingTokenDesigns.length < 10 && paginationOffset !== null) {
      loadMoreData()
    }
  }, [chargingTokenDesigns.length, paginationOffset, loadMoreData])

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchChargingTokenDesignsRequestState === RequestState.ERROR_0_NETWORK ||
      fetchChargingTokenDesignsRequestState ===
        RequestState.ERROR_400_OCCURRED ||
      fetchChargingTokenDesignsRequestState ===
        RequestState.ERROR_401_OCCURRED ||
      fetchChargingTokenDesignsRequestState ===
        RequestState.ERROR_403_OCCURRED ||
      fetchChargingTokenDesignsRequestState ===
        RequestState.ERROR_409_OCCURRED ||
      fetchChargingTokenDesignsRequestState ===
        RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchChargingTokenDesignsRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchChargingTokenDesignsRequestState])
  const handleRetry = () => {
    fetchChargingTokenDesigns(paginationOffset)
  }

  return (
    <div className={Styles.ScrollBarContainer}>
      {chargingTokenDesigns.length === 0 && !isLoading && (
        <div className={Styles.noDesignsFound}>
          <IFText>{t('ChargingTokenPage.NoDesignsFound')}</IFText>
        </div>
      )}

      <Scrollbars
        onScrollFrame={({ scrollHeight, scrollTop, clientHeight }) => {
          if (scrollHeight - scrollTop - clientHeight < 10 && !loadingMore) {
            loadMoreData()
          }
        }}
        renderView={(props) => (
          <div
            {...props}
            style={{
              ...props.style,
              paddingRight: 16, // reserve space for vertical scrollbar
            }}
          />
        )}
      >
        <div className={Styles.gridContainer}>
          {isLoading && chargingTokenDesigns.length === 0
            ? Array.from({ length: 4 }).map((_, index) => (
                <div key={index}>
                  <ChargingTokenDesignCardSkeleton />
                </div>
              ))
            : chargingTokenDesigns.map((design) => (
                <ChargingTokenDesignCard
                  key={design._id}
                  imageData={design.base64Image}
                  designName={design.name}
                  canEdit={canEditToken}
                  assignedDesign={design.assignedToToken}
                  onEdit={() => handleEditDesign(design)}
                  onDelete={() => onDeleteClickHandler(design._id)}
                  height={height}
                />
              ))}

          {loadingMore &&
            fetchChargingTokenDesignsRequestState === RequestState.LOADING &&
            [...Array(1)].map((_, index) => (
              <div key={`loading-${index}`}>
                <ChargingTokenDesignCardSkeleton />
              </div>
            ))}
        </div>

        {failedRequest && (
          <div className={Styles.retryButtonContainer}>
            <IFButton
              size="md"
              color={colors.red}
              text={t('IFTable.TryAgain')}
              onClick={handleRetry}
            />
          </div>
        )}
      </Scrollbars>
      <IFModal
        title={t('ChargingTokenPage.EditDesign')}
        ref={editDesignModalRef}
      >
        <AddChargingTokenDesignModal
          isEdit={canEditToken}
          chargingTokenDesign={selectedDesign}
        />
      </IFModal>

      <IFDialog
        ref={confirmDeleteDialog}
        style={{ margin: '15em' }}
        open={false}
        maxWidth={'xs'}
        fullWidth={true}
        title={t('ChargingTokenPage.deleteModalTitle')}
        bodyText={t('ChargingTokenPage.deleteModalText')}
        buttonAcceptonClick={handleDeleteAdmin}
        buttonCancelColor={Colors.AdminListDeleteModalCancel}
        buttonAcceptColor={Colors.red}
        buttonAcceptText={t('ChargingTokenPage.deleteModalConfirm')}
        buttonClassName={Styles.Button}
        isLoading={
          deleteChargingTokenDesignRequestState === RequestState.LOADING
        }
        dismissOnAccept={false}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  paginationOffset: ChargingTokenDesignSelectors.getPaginationOffset(state),
  editChargingTokenDesignRequestState:
    ChargingTokenDesignSelectors.getEditChargingTokenDesignRequestState(state),
  deleteChargingTokenDesignRequestState:
    ChargingTokenDesignSelectors.getDeleteChargingTokenDesignRequestState(
      state,
    ),
})

const mapDispatchToProps = (dispatch) => ({
  fetchChargingTokenDesigns: (offset) => {
    dispatch(ChargingTokenDesignActions.fetchChargingTokenDesigns(offset))
  },
  deleteChargingTokenDesign: (id) => {
    dispatch(ChargingTokenDesignActions.deleteChargingTokenDesign(id))
  },
})

ChargingTokenDesignGrid.propTypes = {
  chargingTokenDesigns: PropTypes.array.isRequired,
  fetchChargingTokenDesignsRequestState: PropTypes.string.isRequired,
  fetchChargingTokenDesigns: PropTypes.func.isRequired,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChargingTokenDesignGrid)
